import { Component, effect, ElementRef, inject, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideArrowRight } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { filter, map, startWith } from 'rxjs';
import { ManualChapterContent, type ManualChapterLink } from './manual-chapter-content.component';
import { injectManualChapter } from './manual-chapter.resource';
import { ManualHelpService } from './manual-help.service';
import { ManualSearchBox } from './manual-search-box.component';
import { ManualSearchResults } from './manual-search-results.component';
import { ManualSearchService } from './manual-search.service';
import { injectSectionScroll } from './manual-section-scroll';
import type { Tour } from './manual-tour';
import { TourRunnerService } from './manual-tour-runner.service';
import type { ManualSearchResult } from './manual.graphql';
import { ManualService } from './manual.service';

/**
 * Contextual help: the chapter requested through `ManualHelpService.openHelp` in a side sheet, over
 * the current screen (no navigation, so the screen keeps its state). Opened without a chapter (the
 * global help action), it shows only the search box, focused. A search box above the chapter
 * replaces it with the results until one is opened or the search is cleared. Opening a result or a
 * link pushes a location (chapter and section) into the history of `ManualHelpService`, which the
 * back and forward arrows below the search box walk. When the screen has guided tours
 * (`TourRunnerService.currentTours`, at most two: the general one and its own), a "Guided tours"
 * section under the search box replays each one, labelled with its title. It is also where the
 * tours of the screen on display are registered and started, since it is the one manual piece
 * mounted once in the layout. Mount it once in the layout.
 */
@Component({
  selector: 'au-manual-help-panel',
  imports: [
    TranslocoDirective,
    NgIcon,
    HlmButtonImports,
    HlmSheetImports,
    ManualChapterContent,
    ManualSearchBox,
    ManualSearchResults,
  ],
  providers: [
    provideTranslocoScope('manual'),
    provideIcons({ lucideArrowLeft, lucideArrowRight }),
    // Per host: a search typed here never shows on the `/manual` page.
    ManualSearchService,
  ],
  template: `
    <!-- autoFocus is read when the sheet opens: with no chapter, the search box takes the focus. The
         search form sits outside the scoped *transloco view (it only renders once the scope file
         arrives) so the input exists when the dialog applies the focus; it uses the pipe instead. -->
    <hlm-sheet
      [state]="help.isOpen() ? 'open' : 'closed'"
      [autoFocus]="help.chapterId() ? 'first-tabbable' : '[data-testid=manual-search-input]'"
      side="right"
      (closed)="help.close()"
    >
      <hlm-sheet-content
        *hlmSheetPortal="let ctx"
        data-testid="manual-help-panel"
        class="flex w-full flex-col sm:max-w-xl"
      >
        <ng-container *transloco="let t; scope: 'manual'">
          <hlm-sheet-header>
            <h2 hlmSheetTitle>{{ chapter.value()?.title ?? t('manual.help.Title') }}</h2>
            <p hlmSheetDescription>{{ t('manual.help.Description') }}</p>
          </hlm-sheet-header>
        </ng-container>
        <au-manual-search-box class="px-4" />
        <ng-container *transloco="let t; scope: 'manual'">
          @if (tours.currentTours().length > 0) {
            <section
              data-testid="manual-guided-tours"
              class="flex flex-col gap-2 px-4"
            >
              <h3 class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {{ t('manual.help.GuidedTours') }}
              </h3>
              <div class="flex flex-wrap gap-2">
                @for (tour of tours.currentTours(); track tour.id) {
                  <button
                    data-testid="manual-replay-tour"
                    type="button"
                    [attr.aria-label]="t('manual.help.ReplayTour') + ': ' + tour.title"
                    hlmBtn
                    variant="outline"
                    size="sm"
                    (click)="replayTour(tour)"
                  >
                    {{ tour.title }}
                  </button>
                }
              </div>
            </section>
          }
          <div class="flex gap-1 px-4">
            <button
              data-testid="manual-help-back"
              type="button"
              [attr.aria-label]="t('manual.help.Back')"
              [disabled]="!help.canGoBack()"
              hlmBtn
              variant="ghost"
              size="icon-sm"
              (click)="help.back()"
            >
              <ng-icon name="lucideArrowLeft" />
            </button>
            <button
              data-testid="manual-help-forward"
              type="button"
              [attr.aria-label]="t('manual.help.Forward')"
              [disabled]="!help.canGoForward()"
              hlmBtn
              variant="ghost"
              size="icon-sm"
              (click)="help.forward()"
            >
              <ng-icon name="lucideArrowRight" />
            </button>
          </div>
          <div
            #scrollRoot
            data-testid="manual-help-scroll"
            class="min-h-0 flex-1 overflow-y-auto px-4 pb-4"
          >
            @if (search.searching() || search.results()) {
              <au-manual-search-results
                [searching]="search.searching()"
                [results]="search.results()"
                (resultOpen)="openResult($event)"
              />
            } @else if (help.chapterId()) {
              <au-manual-chapter-content
                [chapter]="chapter.value()"
                [loading]="chapter.isLoading()"
                (chapterLink)="open($event)"
              />
            }
          </div>
        </ng-container>
      </hlm-sheet-content>
    </hlm-sheet>
  `,
})
export class ManualHelpPanel {
  protected readonly help = inject(ManualHelpService);
  protected readonly search = inject(ManualSearchService);
  protected readonly tours = inject(TourRunnerService);
  private readonly manual = inject(ManualService);
  private readonly router = inject(Router);
  /** Path of the screen on display, without query string or fragment (the manual's `route`). */
  private readonly screenRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
      map((url) => url.split(/[?#]/)[0]),
    ),
    { requireSync: true },
  );
  protected readonly chapter = injectManualChapter(this.help.chapterId);
  private readonly searchBox = viewChild(ManualSearchBox);
  private readonly scrollRoot = viewChild<ElementRef<HTMLElement>>('scrollRoot');
  private readonly sectionScroll = injectSectionScroll(
    () => (this.chapter.isLoading() ? null : (this.chapter.value()?.id ?? null)),
    () => this.scrollRoot()?.nativeElement ?? null,
    { topWithoutSection: true },
  );

  constructor() {
    // The search input is outside the scoped *transloco view, so its pipe labels would be empty on
    // the first open. Loading the scope now, and on each language change, has them ready by then.
    inject(TranslocoService).selectTranslation('manual').pipe(takeUntilDestroyed()).subscribe();

    // The tours of the screen on display: registered for the replay buttons, and started on the
    // user's first visit. The index may arrive after the navigation, so this re-runs when it does.
    effect(() => {
      const tours = this.manual.toursForRoute(this.screenRoute());
      untracked(() => {
        this.tours.register(tours);
        // The driver highlights elements that must already be in the DOM.
        this.tours.startWhenRendered(tours);
      });
    });

    // Every location of the history, including one reached by back or forward, brings its section
    // into view once its chapter has rendered.
    effect(() => {
      const location = this.help.location();
      if (location) this.sectionScroll.scrollTo(location);
    });

    // A new location (back, forward, an assistant citation) must show, not stay under the results
    // of an abandoned search; one left without a click is reported as such.
    effect(() => {
      if (!this.help.location()) return;
      untracked(() => {
        this.searchBox()?.reset();
        this.search.reset();
      });
    });

    // Closing the panel by any path ends the search; one left without a click is reported.
    effect(() => {
      if (this.help.isOpen()) return;
      this.searchBox()?.reset();
      this.search.reset();
    });
  }

  /** Closes the panel first: the tour highlights elements of the screen the sheet is covering. */
  protected replayTour(tour: Tour): void {
    this.help.close();
    this.tours.replay(tour);
  }

  protected openResult(result: ManualSearchResult): void {
    const { chapterId, sectionId } = this.search.open(result);
    this.help.navigate(chapterId, sectionId);
  }

  /** An internal link opens its chapter, at its section, as a new entry of this panel's history. */
  protected open(link: ManualChapterLink): void {
    this.help.navigate(link.chapterId, link.sectionId);
  }
}
